import { useEffect, useState } from 'react';
import { Button } from 'components';
import { Dialog, DialogContent } from 'components/dialog/dialog';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useSendFeedback } from 'hooks/sendFeedback';
import { useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';
import { PopoverHeader } from './PopoverHeader';
import { FeedbackInput } from './FeedbackInput';

const defaultProblemsState = {
  medicallyIncorrect: false,
  missingImportantInfo: false,
  wronglyAssigned: false,
  tooShort: false,
  tooLong: false,
};

enum OverlayMode {
  question = 'question',
  form = 'form',
  success = 'success',
  error = 'error',
}

interface FeedbackOverlayProps {
  isVisible: boolean;
  setShowFeedbackDialog: (visible: boolean) => void;
  interactionId?: string;
  triggerType?: 'manual' | 'automatic';
  initialHelpful?: boolean;
  onSubmitted?: (isHelpful: boolean) => void;
}

export const FeedbackOverlay = ({
  isVisible,
  setShowFeedbackDialog,
  interactionId,
  triggerType = 'manual',
  initialHelpful,
  onSubmitted,
}: FeedbackOverlayProps) => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const [isHelpful, setIsHelpful] = useState<boolean>();
  const [mode, setMode] = useState<OverlayMode>(OverlayMode.question);

  const { mutate: sendFeedback, isPending: isSendingFeedback } = useSendFeedback(
    (data) => {
      setMode(OverlayMode.success);
      setShowFeedbackInput(false);
      setFeedbackNote('');
      setSelectedProblems({ ...defaultProblemsState });
      toast.success(t('result.feedback.thankYou'));
      if (onSubmitted) {
        onSubmitted(data.isHelpful);
      }
    },
    (error) => {
      console.error('Error sending feedback:', error);
      setMode(OverlayMode.error);
      toast.error(t('result.feedback.sendError', 'Failed to send feedback. Please try again.'));
    }
  );
  // Feedback popup state
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackNote, setFeedbackNote] = useState('');
  const [selectedProblems, setSelectedProblems] = useState<typeof defaultProblemsState>({
    ...defaultProblemsState,
  });

  const handleFeedback = (isHelpful: boolean) => {
    setIsHelpful(isHelpful);
    setShowFeedbackInput(true);
    setMode(OverlayMode.form);
    trackEvent(ANALYTICS_EVENTS.FEEDBACK_HELPFULNESS_CLICKED, {
      triggerType,
      isHelpful,
    });
  };

  const handleProblemChange = (problem: keyof typeof selectedProblems, checked: boolean) => {
    setSelectedProblems((prev) => ({
      ...prev,
      [problem]: checked,
    }));
  };

  const handleFeedbackSubmit = () => {
    if (isHelpful === undefined) {
      return;
    }
    const problems = Object.entries(selectedProblems)
      .filter(([, selected]) => selected)
      .map(([key]) => key);

    trackEvent(ANALYTICS_EVENTS.FEEDBACK_FORM_SUBMITTED, {
      triggerType,
      isHelpful,
      problems,
      hasNote: feedbackNote.trim().length > 0,
    });

    sendFeedback({
      ...(interactionId ? { interactionId } : {}),
      isHelpful,
      problems,
      note: feedbackNote.trim(),
      triggerType,
    });
  };

  useEffect(() => {
    // When opened from the inline result section with a predefined helpfulness,
    // skip the question step and go directly to the form view.
    if (
      isVisible &&
      initialHelpful !== undefined &&
      !showFeedbackInput &&
      mode === OverlayMode.question
    ) {
      setIsHelpful(initialHelpful);
      setShowFeedbackInput(true);
      setMode(OverlayMode.form);
      trackEvent(ANALYTICS_EVENTS.FEEDBACK_HELPFULNESS_CLICKED, {
        triggerType,
        isHelpful: initialHelpful,
      });
    }
  }, [isVisible, initialHelpful, showFeedbackInput, mode, trackEvent, triggerType]);

  const onClose = () => {
    const hasStarted =
      isHelpful !== undefined ||
      feedbackNote.trim().length > 0 ||
      Object.values(selectedProblems).some((v) => v);

    trackEvent(ANALYTICS_EVENTS.FEEDBACK_FORM_CLOSED, {
      triggerType,
      hasStarted,
    });

    setShowFeedbackDialog(false);
    setShowFeedbackInput(false);
    setFeedbackNote('');
    setSelectedProblems({ ...defaultProblemsState });
    setIsHelpful(undefined);
    setMode(OverlayMode.question);
  };

  const onOpenStateChange = (isOpen: boolean) => {
    if (isOpen) {
      setShowFeedbackDialog(true);
      trackEvent(ANALYTICS_EVENTS.FEEDBACK_OPENED, {
        triggerType,
      });
    } else {
      onClose();
    }
  };

  return (
    <Dialog open={isVisible} onOpenChange={onOpenStateChange} data-ph-no-autocapture>
      <DialogContent className="max-w-md bg-white" showCloseButton={false}>
        <div className="flex flex-col">
          {mode === OverlayMode.success ? (
            <div className="mt-4 space-y-3">
              <p className="text-sm font-semibold text-foreground">
                {t('result.feedback.successTitle', 'Vielen Dank für dein Feedback!')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(
                  'result.feedback.successSubtitle',
                  'Dein Feedback hilft uns, den Assistenten weiter zu verbessern.'
                )}
              </p>
              <div className="mt-2">
                <Button size="sm" onClick={onClose} data-ph-no-autocapture>
                  {t('result.feedback.close', 'Schließen')}
                </Button>
              </div>
            </div>
          ) : mode === OverlayMode.error && !showFeedbackInput ? (
            <div className="mt-4 space-y-3">
              <p className="text-sm font-semibold text-destructive">
                {t('result.feedback.errorTitle', 'Feedback konnte nicht gesendet werden')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(
                  'result.feedback.errorSubtitle',
                  'Bitte überprüfe deine Verbindung oder versuche es später erneut.'
                )}
              </p>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="secondary" onClick={onClose} data-ph-no-autocapture>
                  {t('result.feedback.close', 'Schließen')}
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    setMode(OverlayMode.form);
                    setShowFeedbackInput(true);
                  }}
                  data-ph-no-autocapture
                >
                  {t('result.feedback.retry', 'Erneut versuchen')}
                </Button>
              </div>
            </div>
          ) : showFeedbackInput ? (
            <FeedbackInput
              isHelpful={Boolean(isHelpful)}
              selectedProblems={selectedProblems}
              handleProblemChange={handleProblemChange}
              feedbackNote={feedbackNote}
              handleFeedbackCancel={onClose}
              handleFeedbackSubmit={handleFeedbackSubmit}
              isSendingFeedback={isSendingFeedback}
              setFeedbackNote={setFeedbackNote}
            />
          ) : (
            <>
              <PopoverHeader
                title={t('result.feedback.title')}
                subtitle={t('result.feedback.subtitle')}
                onClose={onClose}
              />
              <div className="space-y-3">
                <p className="text-sm font-semibold text-foreground">
                  {t('result.feedback.questionLong')}
                </p>
                <div className="flex space-x-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleFeedback(false)}
                    className="flex-1"
                    data-ph-no-autocapture
                  >
                    {t('result.feedback.no')}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleFeedback(true)}
                    className="flex-1"
                    data-ph-no-autocapture
                  >
                    {t('result.feedback.yes')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
